import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import Portal from '@mui/material/Portal';
import { generateUuid } from '@witmetrics/utils/uuid';
import { isEmpty } from '@witmetrics/utils/misc';
import { useDispatch } from '@witmetrics/data-store';
import { useToggle } from '@/hooks/useToggle';
import { useArray } from '@/hooks/useArray';
import { useAppState } from '@/providers/AppStateProvider';
import Overlay from '@/components/Overlay';
import IconButton from '@/components/IconButton';
import { AddTileIcon, CloseIcon } from '@/icons';
import FileTileGrid from './FileTileGrid';
import type { Tile } from './types';
import { MAX_TILES } from './constants';
import { classes, fetchCollectionData } from './utils';

export type FileCollectionOverlayProps = {
  fileCollectionId: number;
  initialFileId?: number;
  onClose: () => void;
};

export default function FileCollectionOverlay({
  fileCollectionId,
  initialFileId,
  onClose,
}: FileCollectionOverlayProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const [tiles, setTiles, { addItem: addTile }] = useArray<Tile>([], true);
  const [isFetchingData, toggleFetchingData] = useToggle(true);

  useEffect(() => {
    fetchData();
  }, [fileCollectionId]);

  const fetchData = async () => {
    try {
      toggleFetchingData(true);
      const data = await fetchCollectionData(fileCollectionId);
      dispatch.addFileCollections(data.fileCollections);
      dispatch.addFileCollectionFiles(data.files);
      const fileId =
        initialFileId ??
        (!isEmpty(data.files) ? data.files[0].fileId : undefined);
      setTiles([{ tileId: generateUuid(), initialFileId: fileId }]);
      toggleFetchingData(false);
    } catch (err) {
      toggleFetchingData(false);
      onApiError(err, 'Error fetching file data', () => fetchData());
    }
  };

  const handleAddTile = () => {
    if (tiles.length >= MAX_TILES) return;
    addTile({ tileId: generateUuid() });
  };

  const handleCloseTile = (tileId: string) => {
    const next = tiles.filter((t) => t.tileId !== tileId);
    if (isEmpty(next)) return onClose();
    setTiles(next);
  };

  const isWide = tiles.length > 1;
  const canAddTile = tiles.length < MAX_TILES;

  return (
    <>
      <Portal>
        <div className={classes.controls}>
          <IconButton
            titleArrow
            className={classes.closeButton}
            title="Close"
            titlePosition="left"
            onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <IconButton
            titleArrow
            disabled={!canAddTile}
            className={classes.openButton}
            title="Add viewer"
            titlePosition="left"
            onClick={handleAddTile}>
            <AddTileIcon />
          </IconButton>
        </div>
      </Portal>
      <Overlay
        className={twMerge(classes.overlay, isWide && classes.overlayWide)}
        variant="glass"
        onClose={onClose}>
        <div className={classes.grid}>
          {!isFetchingData && (
            <FileTileGrid
              baseCollectionId={fileCollectionId}
              tiles={tiles}
              onCloseTile={handleCloseTile}
            />
          )}
        </div>
      </Overlay>
    </>
  );
}
